import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useConfirm } from '../../components/confirm';
import { BusyIcon } from '../../components/loading';
import { ExportMenu, type ExportMenuHandle } from '../../components/AppToolbar';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { AppDialog } from '../../components/AppDialog';
import { GridSearch } from '../../components/GridSearch';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
// G5's Schedular popup carries the DevExpress Recurrence control; the Pre-Login Task Scheduler already ports it, so this
// screen shares that one editor instead of a second copy (standing rule 8).
import { FRESH, RecurrenceDialog, describeRec, type Rec } from '../settings/preLoginSettings/RecurrenceDialog';

/** One scheduled maintenance task (a live dbo.MaintenanceTaskCheckList row). */
interface TaskRow {
  oid: string;
  taskID?: string;
  category?: string;
  taskDescription?: string;
  ownership?: string;
  recurrence?: string;
  frequency?: string;
  freqCode?: string;
  reminderDays?: string;
  maintenanceId?: string;
  dateToMaintain?: string;
  maintenanceStatus?: number;
}

/** A TaskCheckList master offered in the Link picker. */
interface MasterRow {
  id: string;
  taskID?: string;
  category?: string;
  taskDescription?: string;
  department?: string;
  ownership?: string;
}

/** Today as yyyy-MM-dd in the browser local calendar (toISOString would give the UTC day). */
function localToday(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// Link picker columns — G5's TaskCheckList_ListView_Copy: TaskID · Category · Task/Description · Department · Ownership
// (G6 showed only the first three until the 2026-11-03 side-by-side check; the API already returned the other two).
const PICK_COLUMNS: AppColumn<MasterRow>[] = [
  { field: 'taskID', header: 'TaskID', width: 90 },
  { field: 'category', header: 'Category', width: 150 },
  { field: 'taskDescription', header: 'Task/Description' },
  { field: 'department', header: 'Department', width: 120 },
  { field: 'ownership', header: 'Ownership', width: 140 },
];

/** The legacy "Task Checklist" child grid on the Maintenance Setup detail — the
 *  scheduling engine (Link / Unlink / list / export / #DaysInAdvance). Link picks
 *  TaskCheckList masters, then takes what the G5 Schedular popup takes - Start On and
 *  the full Recurrence control - and the API generates one task per occurrence.
 *  Unlink soft-deletes the not-yet-maintained rows of a series. #DaysInAdvance is
 *  edited in the grid cell, as G5 does, not at link time. Built from the
 *  same shared primitives as the sibling child grids (`.sd-child`, `.ca-btn`,
 *  `.qs-*`, ExportMenu, BusyIcon) so it matches them exactly. */
export function MaintenanceTaskChecklistGrid({ parentId, viewOnly }: {
  parentId: string | null;
  viewOnly: boolean;
}) {
  const notify = useNotify();
  const { confirm } = useConfirm();

  const [rows, setRows] = useState<TaskRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  // Ref to the shared ExportMenu so the Alt+X shortcut can open its dropdown.
  const exportRef = useRef<ExportMenuHandle>(null);

  const load = useCallback(async () => {
    if (!parentId) { setRows([]); return; }
    setLoading(true);
    try {
      const res = await api.get(`/api/v1/maintenance-setups/${parentId}/task-checklists`);
      setRows((res.data?.data ?? []) as TaskRow[]);
    } catch {
      notify.error('Failed to load task checklist.');
    } finally {
      setLoading(false);
    }
  }, [parentId, notify]);

  useEffect(() => { void load(); }, [load]);

  // ── Link picker + recurrence ────────────────────────────────────────────────
  const [linkOpen, setLinkOpen] = useState(false);
  const [masters, setMasters] = useState<MasterRow[]>([]);
  const [pick, setPick] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  // The G5 Schedular popup: Start On (default today) and the Recurrence control. The recurrence starts EMPTY - an empty one
  // is refused ("Please note that recurrence must not be empty."), so nothing is generated until the user sets it.
  const [startOn, setStartOn] = useState(localToday());
  const [rec, setRec] = useState<Rec | null>(null);
  const [recOpen, setRecOpen] = useState(false);

  const openLink = async () => {
    setSearch(''); setPick(new Set());
    setStartOn(localToday()); setRec(null); setRecOpen(false);
    try {
      // With the setup, the list is G5's: this setup's department only, minus checklists already linked to it
      // (a checklist links once per setup).
      const res = await api.get('/api/v1/maintenance-setups/task-checklist-options', { params: { setupId: parentId } });
      setMasters((res.data?.data ?? []) as MasterRow[]);
      setLinkOpen(true);
    } catch {
      notify.error('Failed to load task checklists.');
    }
  };

  const doLink = async () => {
    if (pick.size === 0 || !parentId) return;
    // G5 Dc_Accepting: no recurrence is refused with this wording; the Schedular popup also refuses a Start On in the past.
    if (!rec) {
      notify.warning('Please note that recurrence must not be empty.');
      return;
    }
    if (!startOn || startOn < localToday()) {
      notify.warning('Enter a valid date.');
      return;
    }
    setBusy(true);
    try {
      const dayMode = rec.type >= 2 && rec.monthlyMode === 'day';
      const weekdayMode = rec.type >= 2 && rec.monthlyMode === 'weekday';
      const body = {
        taskChecklistIds: [...pick],
        startOn,
        type: rec.type,
        periodicity: rec.periodicity <= 0 ? 1 : rec.periodicity,
        // Blank whatever the chosen type does not use (the same mapping the Pre-Login Task Scheduler sends).
        weekDays: rec.type === 1 ? rec.weekDays : weekdayMode ? rec.onWeekday : 0,
        weekOfMonth: weekdayMode ? rec.weekOfMonth : 0,
        dayNumber: dayMode ? rec.dayNumber : 0,
        month: rec.type === 3 ? rec.month : 0,
        range: rec.range === 2 ? 2 : 1,
        occurrenceCount: rec.range === 1 ? rec.occurrenceCount : 0,
        recurrenceEnd: rec.range === 2 ? rec.recurrenceEnd : null,
        subject: null,
      };
      const res = await api.post(`/api/v1/maintenance-setups/${parentId}/task-checklists/link`, body);
      const made = res.data?.data?.count ?? 0;
      setLinkOpen(false);
      notify.success(`Linked ${pick.size} checklist(s) — ${made} task(s) generated.`);
      void load();
    } catch (e) {
      // The API refuses a repeat link with a 409 that says which checklist is already on this setup.
      const msg = (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message;
      notify.error(msg ?? 'Link failed.');
    } finally {
      setBusy(false);
    }
  };

  const unlink = async () => {
    if (!selected || !parentId) return;
    const ok = await confirm({
      title: 'Unlink', tone: 'danger', confirmLabel: 'Unlink',
      message: 'Unlink this task series? Its not-yet-maintained tasks are removed; any maintained/skipped ones are kept.',
    });
    if (!ok) return;
    try {
      const res = await api.post(`/api/v1/maintenance-setups/${parentId}/task-checklists/unlink`, { taskOid: selected });
      setSelected(null);
      notify.success(`Unlinked ${res.data?.data?.count ?? 0} task(s).`);
      void load();
    } catch {
      notify.error('Unlink failed.');
    }
  };

  const saveDays = async (row: TaskRow, raw: string) => {
    if (!parentId || raw.trim() === String(row.reminderDays ?? '0')) return;
    const days = Number(raw);
    if (raw.trim() === '' || !Number.isInteger(days) || days < 0 || days > 365) {
      notify.warning('#DaysInAdvance must be a whole number from 0 to 365.');
      void load();   // put the cell back to what is stored
      return;
    }
    try {
      await api.put(`/api/v1/maintenance-setups/${parentId}/task-checklists/days-in-advance`, { taskOid: row.oid, days });
      notify.success('Saved successfully.');
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message;
      notify.error(msg ?? 'Could not save #DaysInAdvance.');
    }
    void load();
  };

  // Export — the current task rows in the legacy column order.
  const COLS: { key: keyof TaskRow; label: string }[] = [
    { key: 'taskID', label: 'TaskID' },
    { key: 'category', label: 'Category' },
    { key: 'taskDescription', label: 'Task/Description' },
    { key: 'ownership', label: 'Ownership' },
    { key: 'recurrence', label: 'Recurrence' },
    { key: 'frequency', label: 'Frequency' },
    { key: 'freqCode', label: 'FreqCode' },
    { key: 'reminderDays', label: '#DaysInAdvance' },
  ];
  const exportRows = rows.map((r) => COLS.map((c) => (r[c.key] == null ? '' : String(r[c.key]))));

  const term = search.trim().toLowerCase();
  const candidates = term
    ? masters.filter((m) => `${m.taskID} ${m.category} ${m.taskDescription} ${m.department} ${m.ownership}`.toLowerCase().includes(term))
    : masters;
  const toggle = (id: string) => setPick((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  // Keyboard shortcuts for this child grid's toolbar (standing rule 14) — each
  // mirrors its button's enabled state.
  useHotkeys([
    { combo: 'alt+r', label: 'Refresh', group: 'Task Checklist', order: 1, disabled: loading, run: () => void load() },
    { combo: 'alt+l', label: 'Link', group: 'Task Checklist', order: 2, disabled: viewOnly || !parentId, run: () => void openLink() },
    { combo: 'alt+k', label: 'Unlink', group: 'Task Checklist', order: 3, disabled: viewOnly || !selected, run: () => void unlink() },
    { combo: 'alt+x', label: 'Export to', group: 'Task Checklist', order: 4, run: () => exportRef.current?.open() },
  ]);

  return (
    // sd-child-bordered frames the whole section (title · toolbar · grid · footer)
    // in a card, same as the EDD Builder Query Details grid (owner request 2026-10-02).
    <div className="sd-static sd-child sd-child-bordered">
      <h3 className="sd-sub-h">Task Checklist</h3>
      <div className="sd-child-bar">
        {/* Icons as G5 and the other child grids carry them (owner screenshot 2026-11-03, "missing icons"): the green
            Refresh glyph (busy spinner while loading) and the Tabler link / unlink pair. */}
        <button type="button" className="ca-btn" disabled={loading} onClick={() => void load()}
          title={`Refresh (${formatCombo('alt+r')})`}>
          {loading ? <BusyIcon busy icon="ti-refresh" /> : <span className="tb-i tb-i-green">🔄</span>} Refresh
        </button>
        <button type="button" className="ca-btn" disabled={viewOnly || !parentId}
          title={`${!parentId ? 'Save the maintenance setup first' : 'Link'} (${formatCombo('alt+l')})`}
          onClick={() => void openLink()}><i className="ti ti-link" aria-hidden="true" /> Link</button>
        <button type="button" className="ca-btn" disabled={viewOnly || !selected}
          title={`Unlink (${formatCombo('alt+k')})`}
          onClick={() => void unlink()}><i className="ti ti-unlink" aria-hidden="true" /> Unlink</button>
        <ExportMenu ref={exportRef} title={`Export to (${formatCombo('alt+x')})`}
          filename="Task Checklist" headers={COLS.map((c) => c.label)} rows={exportRows} />
      </div>
      <div className="sd-static-scroll">
        <table className="sd-static-tbl">
          <thead>
            <tr>
              <th style={{ width: 34 }} aria-label="Select" />
              {COLS.map((c) => <th key={c.key}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.oid}>
                <td style={{ textAlign: 'center' }}>
                  <input type="radio" name="mtc-task" checked={selected === r.oid}
                    onChange={() => setSelected(r.oid)} aria-label="Select row" />
                </td>
                {COLS.map((c) => (
                  <td key={c.key}>
                    {c.key === 'reminderDays' && !viewOnly
                      // #DaysInAdvance is edited in the cell, as G5 does; it applies to every open task of this
                      // setup and frequency, so the grid reloads after it saves.
                      ? <input className="mtc-days" type="number" min={0} max={365} step={1} defaultValue={r.reminderDays ?? '0'}
                          key={`${r.oid}-${r.reminderDays}`} aria-label="#DaysInAdvance"
                          onBlur={(e) => void saveDays(r, e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
                      : (r[c.key] == null ? '' : String(r[c.key]))}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={COLS.length + 1} className="sd-child-empty">
                  {loading ? 'Loading…' : 'No data to display'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {/* No "Total Records" footer: G5's Task Checklist grid has none (side-by-side check 2026-11-03). */}

      {/* Link popup on the shared AppDialog + AppDataGrid (owner request 2026-10-16, "use standard design"):
          Search · a paged multi-select grid of the TaskCheckList masters · the recurrence in the standard
          side-label layout (square, plain white, 26px boxes) · OK / Cancel. */}
      {linkOpen && (
        <AppDialog
          open
          title="Link Task Checklist"
          width={860}
          className="dlg-center inv-24"
          maximizable
          onClose={() => { if (!busy) setLinkOpen(false); }}
          footer={
            <>
              <button type="button" className="ca-btn ca-btn-solid" onClick={() => void doLink()}
                disabled={busy || pick.size === 0}>
                <BusyIcon busy={busy} icon="ti-link" /> OK
              </button>
              <button type="button" className="ca-btn" onClick={() => setLinkOpen(false)} disabled={busy}>Cancel</button>
            </>
          }
        >
          <div className="mtc-top">
            <span className="mtc-count">{pick.size} selected</span>
            {/* Enter here searches — it must not press OK (AppDialog's Enter-to-submit). */}
            <div data-hotkeys-ignore>
              <GridSearch value={search} onChange={setSearch} placeholder="Text to search…" />
            </div>
          </div>
          <AppDataGrid<MasterRow>
            rows={candidates}
            columns={PICK_COLUMNS}
            getRowId={(m) => m.id}
            defaultPageSize={20}
            selectable
            selectedIds={pick}
            onSelectionChange={(s) => setPick(new Set([...s].map(String)))}
            onRowClick={(m) => toggle(m.id)}
            emptyText="No task checklists to link."
          />

          <div className="mtc-rec" data-unsaved-ignore>
            {/* What the G5 Schedular popup asks: Start On, and the Recurrence control behind the "..." button. */}
            <div className="mtc-rec-h">Schedular</div>
            <div className="mtc-grid">
              <label className="mtc-l" htmlFor="mtc-start">Start On:</label>
              <input id="mtc-start" className="mtc-in" type="date" min={localToday()} value={startOn}
                onChange={(e) => setStartOn(e.target.value)} />
              <span className="mtc-l" aria-hidden />
              <span aria-hidden />

              <label className="mtc-l" htmlFor="mtc-rec">Recurrence:</label>
              <div className="mtc-end">
                <input id="mtc-rec" className="mtc-in" readOnly value={rec ? describeRec(rec) : ''}
                  title={rec ? describeRec(rec) : undefined} style={{ width: '100%' }} />
                <button type="button" className="ca-btn" style={{ minWidth: 30 }} onClick={() => setRecOpen(true)}
                  title="Set the recurrence">...</button>
              </div>
            </div>
          </div>
        </AppDialog>
      )}

      {linkOpen && recOpen && (
        <RecurrenceDialog
          // End by is pre-selected and defaults to the start day, as the DevExpress control does in G5; "No end date" is shown
          // but disabled, and Daily's "Every weekday" is not offered.
          value={rec ?? { ...FRESH, range: 2, recurrenceEnd: startOn }}
          options={{ noEnd: 'disabled', longDayLabels: true }}
          onCancel={() => setRecOpen(false)}
          onOk={(r) => { setRec(r); setRecOpen(false); }}
          onRemove={() => { setRec(null); setRecOpen(false); }}
        />
      )}
    </div>
  );
}
